import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiError } from "./api";

const EMPTY = { flow: "export", label: "", rate: "", requires_remark: false, requires_docs: false };

export default function Tds() {
  const [entries, setEntries] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/tds").then((r) => setEntries(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const openDialog = (initial, flow) => {
    setDialog({ open: true, initial });
    setForm(initial
      ? { flow: initial.flow, label: initial.label, rate: initial.rate ?? "", requires_remark: initial.requires_remark, requires_docs: initial.requires_docs }
      : { ...EMPTY, flow });
  };

  const save = async () => {
    try {
      const payload = { ...form, rate: form.rate === "" ? null : parseFloat(form.rate) };
      if (dialog.initial) await api.put(`/tds/${dialog.initial.id}`, payload);
      else await api.post("/tds", payload);
      toast.success("TDS option saved");
      setDialog({ open: false, initial: null });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete TDS option "${t.label}"?`)) return;
    await api.delete(`/tds/${t.id}`);
    toast.success("Deleted");
    load();
  };

  const Section = ({ flow, title, hint }) => (
    <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">{title}</h2>
          <p className="text-xs text-[#8A9A91]">{hint}</p>
        </div>
        <Button variant="outline" size="sm" data-testid={`qc-tds-add-${flow}`} onClick={() => openDialog(null, flow)} className="rounded-full">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add
        </Button>
      </div>
      <ul className="space-y-2">
        {(entries || []).filter((t) => t.flow === flow).map((t) => (
          <li key={t.id} data-testid={`qc-tds-row-${t.id}`} className="flex items-center justify-between rounded-xl border border-[#E8E6E1] px-4 py-3">
            <div>
              <p className="text-sm font-semibold">{t.label}</p>
              <p className="text-xs text-[#8A9A91]">
                {t.requires_remark ? "Remark mandatory" : "No remark needed"}
                {t.requires_docs ? " · Supporting documents mandatory" : ""}
              </p>
            </div>
            <div>
              <button onClick={() => openDialog(t)} className="rounded-full p-2 text-[#8A9A91] hover:text-[#1C3F2B]"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(t)} className="rounded-full p-2 text-[#8A9A91] hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div data-testid="qc-tds-page">
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">Master Data</p>
      <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">TDS Master</h1>
      <p className="mt-1 text-sm text-[#8A9A91]">Government rules change — edit rates here instead of touching code.</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Section flow="export" title="International Invoicing" hint="Shown to Commercial on export cases" />
        <Section flow="domestic" title="Domestic Invoicing" hint="Shown to Commercial on domestic cases" />
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="qc-tds-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{dialog.initial ? "Edit" : "Add"} TDS Option</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Flow</Label>
              <Select value={form.flow} onValueChange={(v) => setForm({ ...form, flow: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="export">Export</SelectItem>
                  <SelectItem value="domestic">Domestic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Label (e.g. 10.40% or NO)</Label>
              <Input data-testid="qc-tds-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Rate % (leave blank for non-deduction options like "NO")</Label>
              <Input data-testid="qc-tds-rate" type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.requires_remark} onCheckedChange={(v) => setForm({ ...form, requires_remark: !!v })} />
              Remark mandatory when selected
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.requires_docs} onCheckedChange={(v) => setForm({ ...form, requires_docs: !!v })} />
              Supporting documents mandatory when selected
            </label>
            <Button data-testid="qc-tds-save" onClick={save} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
